import { useRef, useState } from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { api, photoUrl } from '@/lib/api';
import { buildPhotoVariants } from '@/lib/image';
import { cn } from '@/lib/cn';

interface Props {
  value: string | null;
  onChange: (key: string | null) => void;
}

export default function PhotoUploader({ value, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const url = photoUrl(value);

  async function handleFile(file: File) {
    setError(null);
    setUploading(true);
    try {
      const variants = await buildPhotoVariants(file);
      const { key } = await api.adminUploadPhoto(variants);
      onChange(key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nahrávání selhalo');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="space-y-2">
      <div
        className={cn(
          'relative aspect-[4/3] w-full overflow-hidden rounded-2xl border-2 border-dashed border-border bg-accent-soft/40',
          url && 'border-solid',
        )}
      >
        {url ? (
          <img src={url} alt="" className="h-full w-full object-cover" />
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-full w-full flex-col items-center justify-center gap-2 text-text-muted transition-colors hover:text-accent"
          >
            <ImagePlus size={32} />
            <span className="text-sm">Nahrát fotku</span>
          </button>
        )}
        {url && !uploading && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white transition-colors hover:bg-black/80"
            aria-label="Odebrat fotku"
          >
            <X size={16} />
          </button>
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white">
            <Loader2 className="animate-spin" />
          </div>
        )}
      </div>

      {url && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-sm text-accent hover:underline"
        >
          Vyměnit fotku
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
